import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Car, Mail, User, Loader2, ArrowLeft, CheckCircle2, AlertCircle
} from 'lucide-react';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [devResetUrl, setDevResetUrl] = useState('');

  // Formatar CPF enquanto digita
  const formatCPF = (value) => {
    const cpfClean = value.replace(/\D/g, '').slice(0, 11);
    if (cpfClean.length <= 3) return cpfClean;
    if (cpfClean.length <= 6) return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3)}`;
    if (cpfClean.length <= 9) return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6)}`;
    return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6, 9)}-${cpfClean.slice(9)}`;
  };

  const isEmail = (value) => value.includes('@');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!identifier.trim()) {
      toast.error('Informe o CPF ou e-mail');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        identifier: identifier.includes('@') 
          ? identifier.trim() 
          : identifier.replace(/\D/g, '')
      });
      
      setSubmitted(true);
      toast.success('Verifique seu e-mail!');
      
      // Em modo dev, mostrar URL de reset
      if (res.data._dev_reset_url) {
        setDevResetUrl(res.data._dev_reset_url);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao processar solicitação';
      
      if (error.response?.status === 429) {
        const match = detail.match(/(\d+) segundos/);
        if (match) {
          toast.error(`Aguarde ${match[1]} segundos antes de tentar novamente.`);
        } else {
          toast.error(detail);
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  // Tela de sucesso
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">E-mail Enviado!</h2>
          <p className="text-muted-foreground mb-6">
            Se o CPF/e-mail informado estiver cadastrado, você receberá um link para redefinir sua senha.
          </p>
          
          <Alert className="mb-6 text-left">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Dicas:</strong>
              <ul className="list-disc ml-4 mt-2 space-y-1 text-sm">
                <li>Verifique sua caixa de entrada e spam</li>
                <li>O link expira em 1 hora</li>
                <li>Caso não receba, aguarde 60 segundos e tente novamente</li>
              </ul>
            </AlertDescription>
          </Alert>
          
          {/* Link em modo dev */}
          {devResetUrl && (
            <Alert className="mb-4 text-left border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>[Modo Desenvolvimento]</strong>
                <p className="text-xs mt-1 break-all">
                  <a 
                    href={devResetUrl} 
                    className="underline hover:text-blue-600"
                  >
                    Clique aqui para redefinir senha
                  </a>
                </p>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => {
                setSubmitted(false);
                setDevResetUrl('');
              }}
            >
              Enviar novamente
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => navigate('/client/login')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar para Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <Car className="w-12 h-12 text-primary" />
        </div>
        <h1 className="text-2xl font-bold text-center mb-1">Esqueceu a senha?</h1>
        <p className="text-center text-muted-foreground mb-6">
          Informe seu CPF ou e-mail para receber o link de redefinição.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="identifier">CPF ou E-mail</Label>
            <div className="relative">
              {isEmail(identifier) ? (
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              ) : (
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              )}
              <Input
                id="identifier"
                placeholder="000.000.000-00 ou email@exemplo.com"
                className="pl-10"
                value={identifier}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!value.includes('@') && /^\d/.test(value.replace(/\D/g, ''))) {
                    setIdentifier(formatCPF(value));
                  } else {
                    setIdentifier(value);
                  }
                }}
                required
                data-testid="forgot-identifier-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              'Enviar link de redefinição'
            )}
          </Button>

          <Button 
            type="button"
            variant="ghost" 
            className="w-full"
            onClick={() => navigate('/client/login')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Login
          </Button>
        </form>
      </Card>
    </div>
  );
}
